import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { horasMinutos, topeAnual } from '@/lib/fichajes';

/**
 * Horas por encima de lo pactado, por persona y mes.
 *
 * El exceso lo calcula el servidor (orientativo: no descuenta festivos ni
 * vacaciones). El admin decide si se pagan o se compensan con descanso; la
 * decisión sale en el resumen mensual de la persona. Cada decisión se guarda
 * aparte: la última manda y las anteriores quedan. A tiempo parcial son horas
 * complementarias y no hay nada que decidir aquí.
 */
const DECISION = { pago: 'se pagan', descanso: 'se compensan con descanso' };

/** `refresco` cambia cuando se corrige o se aprueba algo en Equipo: entonces
 *  hay que volver a pedir las horas del mes, o se quedarían las de antes. */
export function ExcesosDelMes({ mesInicial, refresco }) {
  const [mes, setMes] = useState(mesInicial);
  const [datos, setDatos] = useState(null);
  const [ocupada, setOcupada] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/fichajes/excesos', { params: { mes } });
      setDatos(r.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se han podido cargar las horas del mes');
    }
  }, [mes]);

  useEffect(() => { if (mes) cargar(); }, [mes, cargar, refresco]);

  const decidir = async (persona, compensacion) => {
    setOcupada(persona.id);
    try {
      await axiosInstance.post('/fichajes/horas-extra', { user_id: persona.id, periodo: mes, compensacion });
      toast.success(`Anotado: ${DECISION[compensacion]}. Saldrá en su resumen del mes.`);
      cargar();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se ha podido guardar');
    } finally {
      setOcupada(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-end justify-between gap-3 space-y-0">
        <CardTitle className="text-base">Horas del mes y horas extra</CardTitle>
        <Input type="month" value={mes} onChange={(e) => e.target.value && setMes(e.target.value)} className="w-44" />
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {!datos ? <p className="text-slate-500 text-sm">Cargando…</p> : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b">
                <th className="py-2 pr-3 font-medium">Persona</th>
                <th className="py-2 pr-3 font-medium">Trabajado</th>
                <th className="py-2 pr-3 font-medium">Por encima de lo pactado</th>
                <th className="py-2 pr-3 font-medium">Extra en el año</th>
                <th className="py-2 font-medium">Decisión</th>
              </tr>
            </thead>
            <tbody className="tabular-nums">
              {datos.personas.map((p) => {
                const tope = topeAnual(p.exceso_anual_minutos);
                return (
                  <tr key={p.id} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3">
                      {p.name}
                      <span className="block text-xs text-slate-500">
                        {p.jornada_semanal_horas} h/semana{p.jornada_parcial ? ' · parcial' : ''}
                      </span>
                    </td>
                    <td className="py-2 pr-3">{horasMinutos(p.total_minutos)}</td>
                    <td className="py-2 pr-3">
                      {horasMinutos(p.exceso_minutos)}
                      <span className="block text-xs text-slate-500">{p.etiqueta_exceso}</span>
                    </td>
                    <td className={`py-2 pr-3 ${tope.nivel === 'supera' ? 'text-red-700 font-semibold' : tope.nivel === 'cerca' ? 'text-amber-700 font-semibold' : ''}`}>
                      {p.jornada_parcial ? '-' : tope.texto}
                    </td>
                    <td className="py-2">
                      {p.decision && (
                        <span className="block text-xs text-slate-700 mb-1">
                          {horasMinutos(p.decision.minutos)} h {DECISION[p.decision.compensacion]}
                        </span>
                      )}
                      {datos.puede_decidir && !p.jornada_parcial && p.exceso_minutos > 0 && (
                        <span className="flex gap-2 flex-wrap">
                          <Button size="sm" variant="outline" disabled={ocupada === p.id}
                            onClick={() => decidir(p, 'pago')}>Pagar</Button>
                          <Button size="sm" variant="outline" disabled={ocupada === p.id}
                            onClick={() => decidir(p, 'descanso')}>Compensar con descanso</Button>
                        </span>
                      )}
                      {p.jornada_parcial && p.exceso_minutos > 0 && (
                        <span className="text-xs text-slate-500">Complementarias: se totalizan en su resumen</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <p className="text-xs text-slate-500 mt-3">
          Orientativo: compara lo trabajado con la jornada pactada y no descuenta festivos ni
          vacaciones. El tope legal es de 80 horas extraordinarias al año (art. 35.2 ET).
        </p>
      </CardContent>
    </Card>
  );
}
